import type { CoachingNote, Player } from "@/lib/types";

/**
 * Pure roster math — KPI tile values + the search/filter predicate.
 * Extracted from the component so it's unit-testable without a DOM.
 * Ports legacy `js/coaching/roster.js`'s `_coachRosterKpis` and
 * `_filteredCoachPlayers`.
 */

export type RosterStatusFilter = "all" | "active" | "inactive";

export type RosterKpis = {
  /** Count of players with `active === true`. */
  activePlayers: number;
  /** Total linked accounts across ALL players. */
  linkedAccounts: number;
  /** Of those links, how many were created in the last 7 days. */
  linkedAccountsThisWeek: number;
  /** Active players with zero linked accounts. */
  withoutLink: number;
  /** Mean notes-per-active-player, or `null` when there are no
   * active players (the tile renders "—"). */
  notesPerPlayer: number | null;
};

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Compute the four KPI-tile values. KPIs reflect the FULL roster,
 * not the filtered view (legacy behavior). */
export function computeRosterKpis(
  players: Player[],
  notes: CoachingNote[],
): RosterKpis {
  const activePlayers = players.filter((p) => p.active);
  const linkedAccounts = players.reduce(
    (sum, p) => sum + (p.links?.length ?? 0),
    0,
  );

  const cutoff = Date.now() - WEEK_MS;
  let linkedAccountsThisWeek = 0;
  for (const p of players) {
    for (const l of p.links ?? []) {
      const t = l.created_at ? Date.parse(l.created_at) : NaN;
      if (Number.isFinite(t) && t >= cutoff) linkedAccountsThisWeek += 1;
    }
  }

  const withoutLink = activePlayers.filter(
    (p) => (p.links?.length ?? 0) === 0,
  ).length;

  // Notes-per-player: tally how many notes mention each ACTIVE
  // player via `player_ids`. A note can reference several players;
  // team-level notes (no player_ids) are attributed to no one.
  let notesPerPlayer: number | null = null;
  if (activePlayers.length > 0) {
    const activeIds = new Set(activePlayers.map((p) => String(p.id)));
    let total = 0;
    for (const n of notes) {
      for (const pid of n.player_ids ?? []) {
        if (activeIds.has(String(pid))) total += 1;
      }
    }
    notesPerPlayer = total / activePlayers.length;
  }

  return {
    activePlayers: activePlayers.length,
    linkedAccounts,
    linkedAccountsThisWeek,
    withoutLink,
    notesPerPlayer,
  };
}

/** Format a notes-per-player average for the tile — one decimal,
 * or "—" when there are no active players. */
export function formatNotesPerPlayer(value: number | null): string {
  if (value == null) return "—";
  return (Math.round(value * 10) / 10).toFixed(1);
}

/** Apply the search query + status filter to the roster. Never
 * mutates the input. The search haystack matches name, jersey,
 * position, notes, and any
 * linked account's username / display name — same fields the
 * legacy `_filteredCoachPlayers` searched. */
export function filterRoster(
  players: Player[],
  search: string,
  statusFilter: RosterStatusFilter,
): Player[] {
  const q = search.trim().toLowerCase();
  return players.filter((p) => {
    if (statusFilter === "active" && !p.active) return false;
    if (statusFilter === "inactive" && p.active) return false;
    if (!q) return true;
    const haystack = [
      p.display_name ?? "",
      p.jersey_number ?? "",
      p.position ?? "",
      p.notes ?? "",
      ...(p.links ?? []).flatMap((l) => [
        l.username ?? "",
        l.display_name ?? "",
      ]),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}
